// utils.js - Updated for Firestore with proper update logic
import { signInWithPopup, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, arrayUnion, setDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth, googleProvider, firestore, storage } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import JSZip from 'jszip';

// Configure PDF.js worker - use unpkg CDN (more reliable)
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/******************* CV DATA EXTRACTION **************************/


// Helper: Extract text from Word XML
const extractTextFromWordXML = (xmlString) => {
  const regex = /<w:t[^>]*>([^<]*)<\/w:t>/g;
  let match;
  let text = '';
  while ((match = regex.exec(xmlString)) !== null) {
    text += match[1] + ' ';
  }
  return text.trim();
};

// Helper: Extract text from binary DOC file (fallback)
const extractTextFromBinaryBuffer = (arrayBuffer) => {
  const view = new Uint8Array(arrayBuffer);
  let text = '';
  let currentWord = '';

  for (let i = 0; i < view.length; i++) {
    const byte = view[i];

    // ASCII printable range (32-126) or newlines
    if ((byte >= 32 && byte <= 126) || byte === 10 || byte === 13) {
      currentWord += String.fromCharCode(byte);
    } else {
      // Non-printable byte = end of word
      if (currentWord.length > 2) {
        text += currentWord + ' ';
      }
      currentWord = '';
    }
  }

  // Add last word
  if (currentWord.length > 2) {
    text += currentWord;
  }

  // Clean up
  return text.replace(/[^\w\s@.\-+()]/g, ' ').replace(/\s+/g, ' ').trim();
};

// Extract text from PDF files
const extractPdfText = async (file) => {
  // console.log('📄 Extracting text from PDF...');

  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const numPages = pdf.numPages;
    // console.log(`📄 PDF has ${numPages} pages`);

    let fullText = '';

    // Extract text from each page
    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n';
    }

    // Check if we extracted meaningful text
    const trimmedText = fullText.trim();
    if (trimmedText.length < 50) {
      // PDF has very little text, likely image-based
      // console.log('⚠️ PDF appears to be image-based (less than 50 characters extracted)');
      return {
        success: false,
        data: null,
        status: 'failed_image_based',
        message: 'Unable to extract text from your CV. It appears to be an image-based or scanned document. Please upload a text-based PDF version of your CV for better processing. You can convert your CV by exporting it as PDF from Word/Google Docs, or use online PDF converters.'
      };
    }

    // console.log(`✅ Successfully extracted ${trimmedText.length} characters from PDF`);
    return {
      success: true,
      data: trimmedText,
      status: 'success',
      message: 'CV text extracted successfully'
    };

  } catch (error) {
    console.error('❌ Error extracting PDF text:', error);
    return {
      success: false,
      data: null,
      status: 'extraction_failed',
      message: 'Failed to extract text from PDF. The file may be corrupted or password-protected.'
    };
  }
};

// Extract text from DOCX files (with headers and footers)
const extractDocxText = async (file) => {
  // console.log('📄 Extracting text from DOCX (including headers/footers)...');

  try {
    const arrayBuffer = await file.arrayBuffer();

    // Step 1: Extract body text with Mammoth
    const mammothResult = await mammoth.extractRawText({ arrayBuffer });
    const bodyText = mammothResult.value.trim();

    // Step 2: Extract headers and footers using JSZip
    let headerText = '';
    let footerText = '';

    try {
      const zip = new JSZip();
      const docxContent = await zip.loadAsync(arrayBuffer);

      // Extract headers (header1.xml, header2.xml, etc.)
      for (let i = 1; i <= 10; i++) {
        const headerFile = docxContent.file(`word/header${i}.xml`);
        if (headerFile) {
          const headerXml = await headerFile.async('string');
          headerText += extractTextFromWordXML(headerXml) + ' ';
        }
      }

      // Extract footers (footer1.xml, footer2.xml, etc.)
      for (let i = 1; i <= 10; i++) {
        const footerFile = docxContent.file(`word/footer${i}.xml`);
        if (footerFile) {
          const footerXml = await footerFile.async('string');
          footerText += extractTextFromWordXML(footerXml) + ' ';
        }
      }

      // console.log(`✅ Extracted headers: ${headerText.length} chars, footers: ${footerText.length} chars`);
    } catch (zipError) {
      console.warn('⚠️ Could not extract headers/footers:', zipError);
    }

    // Step 3: Combine all text (headers often contain contact info)
    const fullText = (headerText + ' ' + bodyText + ' ' + footerText).trim();

    if (fullText.length < 50) {
      // console.log('⚠️ DOCX has very little text');
      return {
        success: false,
        data: null,
        status: 'failed_empty',
        message: 'Unable to extract sufficient text from your DOCX file. Please ensure your CV contains text content.'
      };
    }

    // console.log(`✅ Successfully extracted ${fullText.length} characters from DOCX`);
    return {
      success: true,
      data: fullText,
      status: 'success',
      message: 'CV text extracted successfully (including headers/footers)'
    };

  } catch (error) {
    console.error('❌ Error extracting DOCX text:', error);
    return {
      success: false,
      data: null,
      status: 'extraction_failed',
      message: 'Failed to extract text from DOCX file. The file may be corrupted.'
    };
  }
};

// Main CV extraction function
export const extractCVData = async (file) => {
  // console.log('🔍 Starting CV data extraction for:', file.name);

  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.split('.').pop();

  if (fileExtension === 'pdf') {
    return await extractPdfText(file);
  } else if (fileExtension === 'docx') {
    return await extractDocxText(file);
  } else if (fileExtension === 'doc') {
    // DOC format: Try Mammoth first, then fall back to binary extraction
    // console.log('📄 Attempting DOC extraction...');

    try {
      // Approach 1: Try Mammoth with DOC file
      const arrayBuffer = await file.arrayBuffer();
      let extractedText = '';

      try {
        // console.log('🔧 Trying Mammoth extraction for DOC...');
        const mammothResult = await mammoth.extractRawText({ arrayBuffer });
        extractedText = mammothResult.value.trim();
        // console.log(`📊 Mammoth extracted ${extractedText.length} characters from DOC`);
      } catch (mammothError) {
        console.warn('⚠️ Mammoth failed for DOC, will try binary fallback:', mammothError.message);
      }

      // Approach 2: Binary fallback if Mammoth failed or returned insufficient text
      if (extractedText.length < 100) {
        // console.log('🔧 Using binary text scanning fallback for DOC...');
        extractedText = extractTextFromBinaryBuffer(arrayBuffer);
        // console.log(`📊 Binary extraction found ${extractedText.length} characters`);
      }

      // Check if we got meaningful text
      if (extractedText.length < 50) {
        // console.log('⚠️ DOC extraction yielded insufficient text');
        return {
          success: false,
          data: null,
          status: 'failed_empty',
          message: 'Unable to extract sufficient text from your DOC file. Please upload a PDF or DOCX format for better text extraction.'
        };
      }

      // console.log(`✅ Successfully extracted ${extractedText.length} characters from DOC`);
      return {
        success: true,
        data: extractedText,
        status: 'success',
        message: 'CV text extracted successfully from DOC file'
      };

    } catch (error) {
      console.error('❌ Error extracting DOC text:', error);
      return {
        success: false,
        data: null,
        status: 'extraction_failed',
        message: 'Failed to extract text from DOC file. Please upload a PDF or DOCX format instead.'
      };
    }
  } else {
    // console.log('⚠️ Unsupported file format:', fileExtension);
    return {
      success: false,
      data: null,
      status: 'unsupported_format',
      message: 'Unsupported file format. Please upload PDF, DOC, or DOCX files only.'
    };
  }
};

// Extract CV data with progress callback
export const extractCVDataWithProgress = async (file, onProgress) => {
  try {
    // Update progress: Starting extraction
    if (onProgress) onProgress({ stage: 'starting', message: 'Starting CV text extraction...' });

    // console.log('🔍 Starting CV data extraction for:', file.name);

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();

    let extractionResult;

    if (fileExtension === 'pdf') {
      if (onProgress) onProgress({ stage: 'extracting', message: 'Extracting text from PDF...' });
      extractionResult = await extractPdfText(file);
    } else if (fileExtension === 'docx') {
      if (onProgress) onProgress({ stage: 'extracting', message: 'Extracting text from DOCX...' });
      extractionResult = await extractDocxText(file);
    } else if (fileExtension === 'doc') {
      if (onProgress) onProgress({ stage: 'extracting', message: 'Extracting text from DOC file...' });

      try {
        // Approach 1: Try Mammoth with DOC file
        const arrayBuffer = await file.arrayBuffer();
        let extractedText = '';

        try {
          // console.log('🔧 Trying Mammoth extraction for DOC...');
          const mammothResult = await mammoth.extractRawText({ arrayBuffer });
          extractedText = mammothResult.value.trim();
          // console.log(`📊 Mammoth extracted ${extractedText.length} characters from DOC`);
        } catch (mammothError) {
          console.warn('⚠️ Mammoth failed for DOC, will try binary fallback:', mammothError.message);
        }

        // Approach 2: Binary fallback if Mammoth failed or returned insufficient text
        if (extractedText.length < 100) {
          if (onProgress) onProgress({ stage: 'extracting', message: 'Using advanced binary extraction for DOC...' });
          // console.log('🔧 Using binary text scanning fallback for DOC...');
          extractedText = extractTextFromBinaryBuffer(arrayBuffer);
          // console.log(`📊 Binary extraction found ${extractedText.length} characters`);
        }

        // Check if we got meaningful text
        if (extractedText.length < 50) {
          // console.log('⚠️ DOC extraction yielded insufficient text');
          extractionResult = {
            success: false,
            data: null,
            status: 'failed_empty',
            message: 'Unable to extract sufficient text from your DOC file. Please upload a PDF or DOCX format for better text extraction.'
          };
        } else {
          // console.log(`✅ Successfully extracted ${extractedText.length} characters from DOC`);
          extractionResult = {
            success: true,
            data: extractedText,
            status: 'success',
            message: 'CV text extracted successfully from DOC file'
          };
        }
      } catch (error) {
        console.error('❌ Error extracting DOC text:', error);
        extractionResult = {
          success: false,
          data: null,
          status: 'extraction_failed',
          message: 'Failed to extract text from DOC file. Please upload a PDF or DOCX format instead.'
        };
      }
    } else {
      if (onProgress) onProgress({ stage: 'unsupported', message: 'Unsupported file format' });
      extractionResult = {
        success: false,
        data: null,
        status: 'unsupported_format',
        message: 'Unsupported file format. Please upload PDF, DOC, or DOCX files only.'
      };
    }

    // Update progress: Completed
    if (onProgress) {
      if (extractionResult.success) {
        onProgress({ stage: 'completed', message: 'Text extraction completed successfully!' });
      } else {
        onProgress({ stage: 'failed', message: extractionResult.message });
      }
    }

    return extractionResult;

  } catch (error) {
    console.error('❌ Error in extractCVDataWithProgress:', error);
    if (onProgress) onProgress({ stage: 'error', message: 'Extraction failed due to an error' });

    return {
      success: false,
      data: null,
      status: 'extraction_failed',
      message: 'Failed to extract text. The file may be corrupted.'
    };
  }
};

/******************* END CV DATA EXTRACTION **************************/

// Authentication functions
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return {
      success: true,
      user: result.user
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Utility function to sanitize email for Firestore document ID
export const sanitizeEmail = (email) => {
  return email.replace(/\./g, '_').replace(/[#$\[\]@]/g, '');
};

// Helper function to get content type based on file extension
const getContentTypeFromFile = (file) => {
  const fileName = file.name.toLowerCase();
  const extension = fileName.split('.').pop();
  
  const contentTypeMap = {
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  };
  
  return contentTypeMap[extension] || file.type || 'application/octet-stream';
};

// Check if candidate exists and handle accordingly
export const checkCandidateStatus = async (userEmail) => {
  // console.log('🔍 Checking candidate status for:', userEmail);

  try {
    // Step 1: Search for email across ALL candidatelist documents (candidatelist2, candidatelist3, etc.)
    const emailExists = await findEmailInCandidateLists(userEmail);

    if (emailExists) {
      // Step 2: Email found - fetch existing candidate data using SANITIZED email
      // console.log('🔍 Fetching existing candidate data...');
      const sanitizedEmail = sanitizeEmail(userEmail);
      // console.log('🧹 Sanitized email:', sanitizedEmail);
      const candidateDataRef = doc(firestore, FIRESTORE_PATHS.collection, sanitizedEmail);
      const candidateDataDoc = await getDoc(candidateDataRef);

      if (candidateDataDoc.exists()) {
        const existingData = candidateDataDoc.data();
        // console.log('✅ Existing candidate data found:', existingData);

        return {
          isNewCandidate: false,
          existingData: existingData,
          message: 'Welcome back! Your information has been loaded.'
        };
      } else {
        // Email in array but no detailed data found
        // console.log('⚠️ Email in array but no detailed data found');
        return {
          isNewCandidate: true,
          existingData: null,
          message: 'Fill the form below to get matched to our current and future opportunities'
        };
      }
    } else {
      // Step 3: Email not found in any list - this is handled in saveCandidateData
      // console.log('🆕 New candidate - email will be added on form submission');

      return {
        isNewCandidate: true,
        existingData: null,
        message: 'Fill the form below to get matched to our current and future opportunities'
      };
    }

  } catch (error) {
    console.error('❌ Error checking candidate status:', error);
    return {
      isNewCandidate: true,
      existingData: null,
      error: error.message,
      message: 'Unable to verify your status. Please proceed with the form.'
    };
  }
};

// File upload function
// File upload function with improved delete logic
export const uploadCVFile = async (file, userEmail, existingCvUrl = null) => {
  // console.log('📁 DEBUG: Starting uploadCVFile function');
  // console.log('📄 File:', file?.name, file?.size, file?.type);
  // console.log('📧 User Email:', userEmail);
  // console.log('🗂️ Existing CV URL:', existingCvUrl);
  
  try {
    // Validate file type early
    const contentType = getContentTypeFromFile(file);
    // console.log('📎 Content Type:', contentType);
    
    if (contentType === 'application/octet-stream' && !file.type.startsWith('application/')) {
      return {
        success: false,
        error: 'Unsupported file type. Please upload PDF, DOC, or DOCX files only.',
        errorCode: 'unsupported-file-type'
      };
    }

    // Use sanitized email for storage path
    const sanitizedEmail = sanitizeEmail(userEmail);
    // console.log('🧹 Sanitized Email for storage:', sanitizedEmail);
    
    // STEP 1: Delete old file if it exists
    if (existingCvUrl) {
      try {
        // console.log('🗑️ Attempting to delete old CV file...');
        // console.log('🔗 Full existing URL:', existingCvUrl);
        
        // Extract the file path from the download URL
        const url = new URL(existingCvUrl);
        // console.log('🔍 URL pathname:', url.pathname);
        
        // More robust path extraction for Firebase Storage URLs
        const pathMatch = url.pathname.match(/\/o\/(.+?)(\?|$)/);
        // console.log('🎯 Path match result:', pathMatch);
        
        if (pathMatch && pathMatch[1]) {
          const encodedPath = pathMatch[1];
          const oldFilePath = decodeURIComponent(encodedPath);
          // console.log('📁 Decoded old file path:', oldFilePath);
          
          const oldFileRef = storageRef(storage, oldFilePath);
          // console.log('🔗 Old file reference created');
          
          await deleteObject(oldFileRef);
          // console.log('✅ Old CV file deleted successfully');
        } else {
          console.warn('⚠️ Could not extract file path from URL');
        }
      } catch (deleteError) {
        console.warn('⚠️ Could not delete old file:', deleteError.message);
        console.warn('⚠️ Delete error code:', deleteError.code);
        // Don't stop the upload if delete fails
      }
    } else {
      // console.log('📝 No existing CV URL provided - skipping delete');
    }
    
    // STEP 2: Upload new file
    const storagePath = `CandidateSourcing/${sanitizedEmail}/cvFile/${file.name}`;
    // console.log('🗄️ New storage path:', storagePath);
    
    // Create a reference to the file location
    const fileRef = storageRef(storage, storagePath);
    // console.log('🔗 Storage Reference Created');
    
    // console.log('📤 Starting file upload...');
   
    // Upload the file with proper metadata
    const snapshot = await uploadBytes(fileRef, file, {
      contentType: contentType,
      customMetadata: {
        uploadedBy: userEmail,
        uploadedAt: new Date().toISOString(),
        originalFileName: file.name,
        fileSize: file.size.toString()
      }
    });
    // console.log('✅ File uploaded successfully, snapshot:', snapshot);

    // console.log('🔗 Getting download URL...');
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    // console.log('✅ Download URL obtained:', downloadURL);

    // Return upload success immediately - extraction will happen separately
    const result = {
      success: true,
      downloadURL,
      fileName: file.name,
      contentType: contentType,
      fileSize: file.size,
      file: file, // Pass the file object for later extraction
      extractionPending: true // Flag to indicate extraction needs to happen
    };

    // console.log('🎉 Upload completed successfully:', result);
    return result;
    
  } catch (error) {
    console.error('❌ FILE UPLOAD ERROR:');
    console.error('Error message:', error.message);
    console.error('Error code:', error.code);
    console.error('Full error:', error);
    
    // Enhanced error handling for specific Firebase Storage errors
    let userFriendlyError = error.message;
    
    if (error.code === 'storage/unauthorized') {
      userFriendlyError = 'You do not have permission to upload files. Please try signing out and signing back in.';
    } else if (error.code === 'storage/quota-exceeded') {
      userFriendlyError = 'Storage quota exceeded. Please contact support.';
    } else if (error.code === 'storage/unauthenticated') {
      userFriendlyError = 'Please sign in again to upload files.';
    } else if (error.code === 'storage/retry-limit-exceeded') {
      userFriendlyError = 'Upload failed due to network issues. Please try again.';
    }
    
    return {
      success: false,
      error: userFriendlyError,
      errorCode: error.code,
      originalError: error.message
    };
  }
};

// Save candidate data to Firestore with proper create/update logic
export const saveCandidateData = async (userEmail, formData, cvInfo) => {
  // console.log('🔍 DEBUG: Starting saveCandidateData function (FIRESTORE)');
  // console.log('📧 User Email:', userEmail);
  // console.log('📝 Form Data:', formData);
  // console.log('📄 CV Info:', cvInfo);
  
  try {
    // Use sanitized email as document ID
    const sanitizedEmail = sanitizeEmail(userEmail);
    // console.log('🧹 Sanitized Email:', sanitizedEmail);
    
    const collectionPath = 'CandidateSourcing';
    const documentId = sanitizedEmail;
    
    // console.log('🗄️ Collection:', collectionPath);
    // console.log('📄 Document ID:', documentId);
    
    // Create document reference
    const docRef = doc(firestore, collectionPath, documentId);
    // console.log('🔗 Document Reference Created:', docRef.path);
    
    // Check if document already exists to determine if this is create or update
    const existingDoc = await getDoc(docRef);
    const isUpdate = existingDoc.exists();
    
    // console.log(`🔄 Operation type: ${isUpdate ? 'UPDATE' : 'CREATE'}`);
    
    let candidateData;
    
    if (isUpdate) {
      // For updates, preserve original createdAt and update only changed fields
      const existingData = existingDoc.data();
      candidateData = {
        ...formData,
        cvFileName: cvInfo?.fileName || existingData.cvFileName || null,
        cvDownloadURL: cvInfo?.downloadURL || existingData.cvDownloadURL || null,
        cvRawData: cvInfo?.cvRawData || existingData.cvRawData || null,
        cvExtractionStatus: cvInfo?.cvExtractionStatus || existingData.cvExtractionStatus || null,
        cvExtractionMessage: cvInfo?.cvExtractionMessage || existingData.cvExtractionMessage || null,
        isInterviewDone: existingData.isInterviewDone ?? false, // Preserve existing value
        isMetricDone: existingData.isMetricDone ?? false, // Preserve existing value
        submittedAt: new Date().toISOString(),
        userEmail: userEmail,
        sanitizedEmail: sanitizedEmail,
        createdAt: existingData.createdAt || new Date(),
        updatedAt: new Date()
      };
      // console.log('📝 UPDATE: Preserving createdAt from existing document');
    } else {
      // For new documents, set both createdAt and updatedAt
      candidateData = {
        ...formData,
        cvFileName: cvInfo?.fileName || null,
        cvDownloadURL: cvInfo?.downloadURL || null,
        cvRawData: cvInfo?.cvRawData || null,
        cvExtractionStatus: cvInfo?.cvExtractionStatus || null,
        cvExtractionMessage: cvInfo?.cvExtractionMessage || null,
        isInterviewDone: false, // Default false for new candidates
        isMetricDone: false, // Default false for new candidates
        submittedAt: new Date().toISOString(),
        userEmail: userEmail,
        sanitizedEmail: sanitizedEmail,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      // console.log('📝 CREATE: Setting new createdAt');
    }
    
    // console.log('💾 Data to save:', candidateData);
    // console.log('🚀 Attempting to save to Firestore...');

    // Save to Firestore using setDoc (creates or overwrites document)
    await setDoc(docRef, candidateData);

    // Create Interview subcollection docs if they don't exist
    const skillsRef = doc(firestore, collectionPath, documentId, 'Interview', 'Skills');
    const psychometricRef = doc(firestore, collectionPath, documentId, 'Interview', 'Psychometric');

    const skillsDoc = await getDoc(skillsRef);
    if (!skillsDoc.exists()) {
      await setDoc(skillsRef, { createdAt: new Date() });
      // console.log('✅ Interview/Skills document created');
    }

    const psychometricDoc = await getDoc(psychometricRef);
    if (!psychometricDoc.exists()) {
      await setDoc(psychometricRef, { createdAt: new Date() });
      // console.log('✅ Interview/Psychometric document created');
    }

    // Update email list - only if email doesn't already exist
    // console.log('📋 Checking if email already exists in any candidatelist...');
    const emailExists = await findEmailInCandidateLists(userEmail);

    if (!emailExists) {
      // console.log('📋 Email not found, adding to latest candidatelist...');
      const latestList = await getLatestCandidateList();

      if (latestList.data) {
        // List exists and has space, add email to it
        // console.log(`✅ Adding email to existing list: ${latestList.name}`);
        await updateDoc(latestList.ref, {
          emails: arrayUnion(userEmail),
          updatedAt: new Date()
        });
        // console.log(`✅ Email added to ${latestList.name}`);
      } else {
        // Need to create a new list
        // console.log(`✨ Creating new list: ${latestList.name}`);
        await setDoc(latestList.ref, {
          emails: [userEmail],
          createdAt: new Date(),
          updatedAt: new Date()
        });
        // console.log(`✅ New list ${latestList.name} created with first email`);
      }
    } else {
      // console.log('✅ Email already exists in a candidatelist, skipping duplicate add');
    }

    // console.log(`✅ SUCCESS: Data ${isUpdate ? 'updated' : 'created'} in Firestore successfully!`);
    return {
      success: true,
      isUpdate: isUpdate
    };
    
  } catch (error) {
    console.error('❌ FIRESTORE SAVE ERROR:');
    console.error('Error message:', error.message);
    console.error('Error code:', error.code);
    console.error('Full error:', error);
    console.error('Stack trace:', error.stack);
    
    return { 
      success: false, 
      error: error.message,
      errorCode: error.code,
      fullError: error.toString()
    };
  }
};


/******************* Production Talent Portal **************************/

// Firestore paths for production
const FIRESTORE_PATHS = {
  collection: 'CandidateSourcing',
  emailListPrefix: 'candidatelist',  // Base name: candidatelist2, candidatelist3, etc.
  startingListNumber: 2,              // Start from candidatelist2 (candidatelist is full)
  maxEmailsPerList: 18000             // Safe limit per document (18k instead of 20k limit)
};

// Helper function to get the latest candidatelist document with space
const getLatestCandidateList = async () => {
  // console.log('🔍 Finding latest candidatelist document with available space...');

  let listNumber = FIRESTORE_PATHS.startingListNumber;

  // Check candidatelist2, candidatelist3, candidatelist4, etc.
  while (true) {
    const listName = `${FIRESTORE_PATHS.emailListPrefix}${listNumber}`;
    const listRef = doc(firestore, FIRESTORE_PATHS.collection, listName);

    try {
      const listDoc = await getDoc(listRef);

      if (listDoc.exists()) {
        const data = listDoc.data();
        const emailCount = (data.emails || []).length;
        // console.log(`📋 Checked ${listName}: ${emailCount}/${FIRESTORE_PATHS.maxEmailsPerList} emails`);

        if (emailCount < FIRESTORE_PATHS.maxEmailsPerList) {
          // console.log(`✅ Found available space in ${listName}`);
          return { name: listName, ref: listRef, data: data };
        }

        // This list is full, check next
        // console.log(`⚠️ ${listName} is FULL (${emailCount} emails)`);
        listNumber++;
      } else {
        // This list doesn't exist, so we should create it
        // console.log(`✨ ${listName} does not exist. Will create new list.`);
        return {
          name: listName,
          ref: listRef,
          data: null // Indicates this is a new list to be created
        };
      }
    } catch (error) {
      console.error(`❌ Error checking ${listName}:`, error);
      // If error, assume we need to create this list
      return {
        name: listName,
        ref: listRef,
        data: null
      };
    }
  }
};

// Helper function to search for email across all candidatelist documents
const findEmailInCandidateLists = async (userEmail) => {
  // console.log('🔍 Searching for email across all candidatelist documents...');

  // First check the original 'candidatelist' (without number)
  try {
    const originalListRef = doc(firestore, FIRESTORE_PATHS.collection, FIRESTORE_PATHS.emailListPrefix);
    const originalListDoc = await getDoc(originalListRef);

    if (originalListDoc.exists()) {
      const data = originalListDoc.data();
      const emails = data.emails || [];
      // console.log(`📋 Searching in ${FIRESTORE_PATHS.emailListPrefix} (${emails.length} emails)...`);

      if (emails.includes(userEmail)) {
        // console.log(`✅ Email found in ${FIRESTORE_PATHS.emailListPrefix}!`);
        return true;
      }
    }
  } catch (error) {
    // console.log(`⚠️ Error checking ${FIRESTORE_PATHS.emailListPrefix}:`, error);
  }

  // Then search through candidatelist2, candidatelist3, candidatelist4, etc.
  let listNumber = FIRESTORE_PATHS.startingListNumber;

  while (true) {
    const listName = `${FIRESTORE_PATHS.emailListPrefix}${listNumber}`;
    const listRef = doc(firestore, FIRESTORE_PATHS.collection, listName);

    try {
      const listDoc = await getDoc(listRef);

      if (listDoc.exists()) {
        const data = listDoc.data();
        const emails = data.emails || [];
        // console.log(`📋 Searching in ${listName} (${emails.length} emails)...`);

        if (emails.includes(userEmail)) {
          // console.log(`✅ Email found in ${listName}!`);
          return true;
        }

        listNumber++;
      } else {
        // No more lists to check
        // console.log(`📝 Reached end at ${listName} (does not exist)`);
        break;
      }
    } catch (error) {
      // console.log(`⚠️ Error checking ${listName}, stopping search`);
      break;
    }
  }

  // console.log('❌ Email not found in any candidatelist');
  return false;
};

/********************** Functions  ******************************* */
const functions = getFunctions(undefined, 'asia-south1'); // region must match your deploy


// Function to update claims for existing users
export const updateUserClaims = async () => {
  try {
    // console.log('🔄 Updating user claims...');
    const updateClaims = httpsCallable(functions, 'updateUserClaims');
    const result = await updateClaims();
    // console.log('✅ Claims updated:', result.data);

    // Force token refresh so rules see request.auth.token.sanitizedEmail
    const user = auth.currentUser;
    if (user) {
      await user.getIdToken(true);
      // console.log('✅ Token refreshed with new claims');
    }
    return { success: true, ...result.data };
  } catch (err) {
    console.error('❌ Failed to update claims', err);
    return { success: false, error: err?.message || String(err) };
  }
};

// Use this whenever you need the sanitized email for paths:
export const getSanitizedEmailFromClaims = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  const token = await user.getIdTokenResult(true);
  return token.claims?.sanitizedEmail || null;
};

// Function to check if user has sanitized email claim
export const checkUserClaims = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return false;
    
    const token = await user.getIdTokenResult();
    // console.log('🔍 User claims:', token.claims);
    
    return !!token.claims.sanitizedEmail;
    
  } catch (error) {
    console.error('❌ Error checking claims:', error);
    return false;
  }
};

// Modified login function to handle claims
export const signInWithGoogleAndClaims = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    
    // Wait a moment for the onCreate function to set claims
    setTimeout(async () => {
      // Check if claims exist
      const hasClaims = await checkUserClaims();
      
      if (!hasClaims) {
        // console.log('⚠️ No claims found, updating...');
        await updateUserClaims();
      }
    }, 2000); // Wait 2 seconds for onCreate function

    return {
      success: true,
      user: result.user
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

// Get interview link from Firestore
export const getInterviewLink = async () => {
  try {
    const interviewLinkRef = doc(firestore, 'Links', 'interviewLink');
    const interviewLinkDoc = await getDoc(interviewLinkRef);

    if (!interviewLinkDoc.exists()) {
      return {
        success: false,
        error: 'Interview link not found'
      };
    }

    const data = interviewLinkDoc.data();
    const linkNo = data.linkNo;

    if (!linkNo) {
      return {
        success: false,
        error: 'Link number not configured'
      };
    }

    const linkField = `link${linkNo}`;
    const url = data[linkField];

    if (!url) {
      return {
        success: false,
        error: `Link ${linkNo} not found`
      };
    }

    return {
      success: true,
      url: url
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

export const getPsychometricInterviewLink = async () => {
  try {
    const interviewLinkRef = doc(firestore, 'Links', 'interviewLink');
    const interviewLinkDoc = await getDoc(interviewLinkRef);

    if (!interviewLinkDoc.exists()) {
      return {
        success: false,
        error: 'Psychometric link not found'
      };
    }

    const data = interviewLinkDoc.data();
    const plinkNo = data.plinkNo;

    if (!plinkNo) {
      return {
        success: false,
        error: 'Psychometric link number not configured'
      };
    }

    const plinkField = `plink${plinkNo}`;
    const url = data[plinkField];

    if (!url) {
      return {
        success: false,
        error: `Psychometric link ${plinkNo} not found`
      };
    }

    return {
      success: true,
      url: url
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};


export const extractCompanyAndJobFromUrl = async (url) => {
  try {

    const urlObj = new URL(url);

    const encodedData = urlObj.searchParams.get("data");

    if (!encodedData) return null;

    const decoded = decodeURIComponent(encodedData);
    const parsed = JSON.parse(decoded);
    //  console.log('extracting details', parsed.CompanyName, parsed.JobName)
    return {
      company: parsed.CompanyName,
      jobName: parsed.JobName
    };

  } catch (error) {
    console.error("Error extracting company/job from URL:", error);
    return null;
  }
};

export const updateCandidateListSnapshot = async (type = 'new', email = null) => {
  if (!email) { console.warn('[snapshot] email required'); return; }
 
  try {
    const trackingRef = doc(firestore, 'CandidateSourcing', '000000_tracking');
    const trackSnap   = await getDoc(trackingRef);
    const now         = new Date().toISOString();
 
    if (!trackSnap.exists()) {
      // Bootstrap from candidatelist2 on very first call
      const listSnap = await getDoc(doc(firestore, 'CandidateSourcing', 'candidatelist2'));
      const emails   = listSnap.exists() ? (listSnap.data().emails || []) : [];
      const entries  = emails.map((e, i) => ({
        email:       e,
        createdAt:   now,
        updatedAt:   now,
        indexNumber: i + 1,
      }));
      await setDoc(trackingRef, { entries, createdAt: now, updatedAt: now });
      return;
    }
 
    const entries = [...(trackSnap.data().entries || [])];
    const idx     = entries.findIndex(e => e.email === email);
 
    if (type === 'new') {
      if (idx === -1) {
        // Brand new candidate — append with both timestamps same
        entries.push({
          email,
          createdAt:   now,
          updatedAt:   now,
          indexNumber: entries.length + 1,
        });
      }
      // Already exists but called as 'new' — ignore, don't overwrite createdAt
    } else if (type === 'updated') {
      if (idx !== -1) {
        // Existing candidate resubmitting — only updatedAt changes
        entries[idx] = { ...entries[idx], updatedAt: now };
      } else {
        // Safety: if somehow not in tracking yet, add it
        entries.push({
          email,
          createdAt:   now,
          updatedAt:   now,
          indexNumber: entries.length + 1,
        });
      }
    }
 
    await updateDoc(trackingRef, { entries, updatedAt: now });
  } catch (e) {
    console.error('[updateCandidateListSnapshot]', e);
  }
};